import { lazy, Suspense, useMemo } from 'react';
import type { LucideProps } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';

type IconName = keyof typeof dynamicIconImports;

interface LucideIconProps {
  name: string;
  className?: string;
  fallback?: React.ReactNode;
}

/** Convert PascalCase icon name to kebab-case key used by dynamicIconImports */
function toKebab(str: string): string {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

// Cache lazy components so we don't re-create them on every render
const iconCache = new Map<string, React.LazyExoticComponent<React.ComponentType<LucideProps>>>();

function getIconComponent(kebabName: string) {
  if (iconCache.has(kebabName)) return iconCache.get(kebabName)!;
  const importer = dynamicIconImports[kebabName as IconName];
  if (!importer) return null;
  const LazyIcon = lazy(importer);
  iconCache.set(kebabName, LazyIcon);
  return LazyIcon;
}

export function LucideIcon({ name, className = 'h-4 w-4', fallback }: LucideIconProps) {
  const Icon = useMemo(() => {
    if (!name) return null;
    // Try as-is first (already kebab-case), then convert from PascalCase
    return getIconComponent(name) || getIconComponent(toKebab(name));
  }, [name]);

  if (!Icon) {
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <Suspense fallback={fallback || <span className={className} />}>
      <Icon className={className} />
    </Suspense>
  );
}
